import React from 'react';
import autoBind from 'react-autobind';

import Localization from "../../Localization.jsx";
import ThreeDJobManager, { PHASE } from "../../tools/ThreeDJobManager.js";
import { BRAND_PRIMARY } from "../../theme/brandTheme.js";

import { ActionIcon, Popover, Text, Loader, Group, Badge, UnstyledButton, Stack, Tooltip } from '@mantine/core';
import { IconCube } from '@tabler/icons-react';

// Floating indicator at the top right of the hotspot map: visible while 3D
// generation jobs for the CURRENT locale run in the background. Clicking a job
// re-opens its hotspot's editing panel and the component's edit dialog —
// closing the dialog never loses a job (ThreeDJobManager keeps it running).
class ThreeDJobIndicator extends React.Component {
  constructor(props) {
    super(props);
    this.state = { jobs: [], opened: false, cleaning: false };
    autoBind(this);
  }

  componentDidMount() {
    this._mounted = true;
    this.unsubscribe = ThreeDJobManager.subscribe(this.refreshJobs);
    this.refreshJobs();
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  /** Verify every chip against the server and drop the ones that are done,
   * cancelled, or no longer backed by anything — the stale-snapshot escape
   * hatch (the same sweep also runs automatically at login). */
  async handleCleanup() {
    if (this.state.cleaning) return;
    this.setState({ cleaning: true });
    try {
      await ThreeDJobManager.revalidate();
    } finally {
      if (this._mounted) {
        this.setState({ cleaning: false });
        this.refreshJobs();
      }
    }
  }

  refreshJobs() {
    const epackId = this.props.epackId != null ? this.props.epackId : null;
    this.setState({ jobs: ThreeDJobManager.getActiveJobs(epackId) });
  }

  phaseLabel(job) {
    const strings = Localization.strings;
    switch (job.phase) {
      case PHASE.SUBMITTING: return strings.GEN3D_PHASE_SUBMITTING || 'Submitting...';
      case PHASE.QUEUED: return strings.GEN3D_PHASE_QUEUED || 'Waiting for the server...';
      case PHASE.PREPARING: return strings.GEN3D_PHASE_PREPARING || 'Preparing...';
      case PHASE.GENERATING: return strings.GEN3D_PHASE_GENERATING || 'Building the 3D model...';
      case PHASE.FINALIZING: return strings.GEN3D_PHASE_FINALIZING || 'Finishing up...';
      case PHASE.IMPORTING: return strings.GEN3D_PHASE_IMPORTING || 'Saving the model...';
      case PHASE.CLOUD_GENERATING: return strings.GEN3D_PHASE_CLOUD || 'Generating in the cloud...';
      case PHASE.FAILED: return strings.GEN3D_PHASE_FAILED || 'Generation failed';
      default: return strings.GENERATING_3D_MODEL || 'Generating 3D model...';
    }
  }

  handleJobClicked(job) {
    this.setState({ opened: false });
    if (this.props.onOpenJob) {
      this.props.onOpenJob(job);
    }
  }

  render() {
    const jobs = this.state.jobs;
    if (!jobs || jobs.length === 0) {
      return null;
    }
    const strings = Localization.strings;
    const title = strings.formatString && strings.GEN3D_JOBS_IN_PROGRESS
      ? strings.formatString(strings.GEN3D_JOBS_IN_PROGRESS, jobs.length)
      : jobs.length + " 3D model(s) generating";

    return (
      <div
        style={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}
        onPointerDown={e => e.stopPropagation()}
        onPointerMove={e => e.stopPropagation()}
        onContextMenu={e => e.preventDefault()}
      >
        <Popover
          width={280}
          position="bottom-end"
          withArrow
          shadow="md"
          opened={this.state.opened}
          onChange={(opened) => this.setState({ opened })}
        >
          <Popover.Target>
            <Tooltip label={title} withArrow disabled={this.state.opened}>
              <ActionIcon
                variant="filled"
                color={BRAND_PRIMARY}
                size={44}
                radius="xl"
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.35)' }}
                onClick={() => this.setState({ opened: !this.state.opened })}
              >
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconCube size={22} />
                  <Loader
                    size={40}
                    color="white"
                    type="oval"
                    style={{ position: 'absolute', top: -9, left: -9, opacity: 0.9 }}
                  />
                </div>
              </ActionIcon>
            </Tooltip>
          </Popover.Target>
          <Popover.Dropdown p="xs">
            <Text size="sm" fw={600} mb={6}>{title}</Text>
            <Stack gap={4}>
              {jobs.map((job) => (
                <UnstyledButton
                  key={job.componentId}
                  onClick={() => this.handleJobClicked(job)}
                  style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #eee' }}
                >
                  <Group justify="space-between" wrap="nowrap" gap={8}>
                    <div style={{ minWidth: 0 }}>
                      <Text size="sm" truncate fw={500}>
                        {job.name || (strings.MODEL_3D_DEFAULT_NAME || '3D Model')}
                      </Text>
                      <Text size="xs" c="dimmed" truncate>
                        {this.phaseLabel(job)}
                      </Text>
                    </div>
                    {job.phase === PHASE.FAILED
                      ? <Badge color="red" size="xs">!</Badge>
                      : <Loader size="xs" color={BRAND_PRIMARY} />}
                  </Group>
                </UnstyledButton>
              ))}
            </Stack>
            <Group justify="space-between" align="center" mt={6} gap={8} wrap="nowrap">
              <Text size="xs" c="dimmed">{strings.GEN3D_JOB_OPEN_HINT || 'Click to view progress'}</Text>
              <Tooltip
                label={strings.GEN3D_CLEANUP_HINT || 'Check each job with the server and remove ones that were cancelled or already finished.'}
                withArrow
                position="bottom"
              >
                <UnstyledButton onClick={this.handleCleanup} disabled={this.state.cleaning}>
                  <Text size="xs" c={BRAND_PRIMARY} fw={500} style={{ whiteSpace: 'nowrap' }}>
                    {this.state.cleaning
                      ? (strings.GEN3D_CLEANUP_RUNNING || 'Checking...')
                      : (strings.GEN3D_CLEANUP || 'Clean up')}
                  </Text>
                </UnstyledButton>
              </Tooltip>
            </Group>
          </Popover.Dropdown>
        </Popover>
      </div>
    );
  }
}

export default ThreeDJobIndicator;
